import React, { useState, useEffect } from 'react';
import { GlobalMicroButton } from '../../GlobalMicroButton';
import { StatusBadge } from '../../StatusBadge';
import styles from './FunctionalSpecCard.module.css';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import { useTheme } from '../../../context/ThemeProvider';
import { FunctionalSpecification } from '../../../types';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';

interface FunctionalSpecCardProps {
  spec: FunctionalSpecification;
  projectId: string;
  onEdit?: (spec: FunctionalSpecification) => void;
  onDelete?: (specId: string) => void;
  onViewTraceability?: (spec: FunctionalSpecification) => void;
  onAiEnhance?: (spec: FunctionalSpecification) => void;
  onEntityClick?: (entityId: string, entityType: 'URS' | 'TEST' | 'RISK' | 'SOP') => void;
  compact?: boolean;
}

export const FunctionalSpecCard: React.FC<FunctionalSpecCardProps> = ({
  spec,
  projectId,
  onEdit,
  onDelete,
  onViewTraceability,
  onAiEnhance,
  onEntityClick,
  compact = false
}) => {
  const { t } = useLocale();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isExpanded, setIsExpanded] = useState(false);
  const [entityTitles, setEntityTitles] = useState<Record<string, string>>({});
  const [reverseLinkedEntities, setReverseLinkedEntities] = useState<{
    urs_ids: string[];
    test_ids: string[];
    risk_ids: string[];
    sop_ids: string[];
  }>({
    urs_ids: [],
    test_ids: [],
    risk_ids: [],
    sop_ids: []
  });

  // Fetch entity titles for connected entities (both forward and reverse)
  useEffect(() => {
    const fetchEntityTitles = async () => {
      if (!projectId || !isExpanded) return;

      const titles: Record<string, string> = {};
      const reverseLinks = {
        urs_ids: [] as string[],
        test_ids: [] as string[],
        risk_ids: [] as string[],
        sop_ids: [] as string[]
      };

      // Fetch URS documents - both forward links and reverse links
      try {
        const ursCol = collection(db, 'projects', projectId, 'urs');
        const ursSnapshot = await getDocs(ursCol);
        ursSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const id = data.id || `URS-${doc.id.substring(0, 8)}`;
          const links = data.links || {};
          const fsIds = links.fs_ids || [];

          // If this URS references our FS, add it to reverse links
          if (fsIds.includes(spec.id)) {
            reverseLinks.urs_ids.push(id);
            titles[id] = data.title || id;
          }

          // If this URS is in our forward links, add its title
          if (spec.links.urs_ids.includes(id)) {
            titles[id] = data.title || id;
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching URS titles:', err);
        }
      }

      // Fetch TEST documents - both forward links and reverse links
      try {
        const testCol = collection(db, 'projects', projectId, 'tests');
        const testSnapshot = await getDocs(testCol);
        testSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const id = data.id || `TEST-${doc.id.substring(0, 8)}`;
          const links = data.links || {};
          const fsIds = links.fs_ids || [];

          // If this TEST references our FS, add it to reverse links
          if (fsIds.includes(spec.id)) {
            reverseLinks.test_ids.push(id);
            titles[id] = data.title || id;
          }

          // If this TEST is in our forward links, add its title
          if (spec.links.test_ids.includes(id)) {
            titles[id] = data.title || id;
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching TEST titles:', err);
        }
      }

      // Fetch RISK documents - both forward links and reverse links
      try {
        const riskCol = collection(db, 'projects', projectId, 'risks');
        const riskSnapshot = await getDocs(riskCol);
        riskSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const id = data.id || `RISK-${doc.id.substring(0, 8)}`;
          const links = data.links || {};
          const fsIds = links.fs_ids || [];

          // If this RISK references our FS, add it to reverse links
          if (fsIds.includes(spec.id)) {
            reverseLinks.risk_ids.push(id);
            titles[id] = data.title || id;
          }

          // If this RISK is in our forward links, add its title
          if (spec.links.risk_ids.includes(id)) {
            titles[id] = data.title || id;
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching RISK titles:', err);
        }
      }

      // Fetch SOP documents - both forward links and reverse links
      try {
        const sopCol = collection(db, 'projects', projectId, 'sops');
        const sopSnapshot = await getDocs(sopCol);
        sopSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const id = data.id || `SOP-${doc.id.substring(0, 8)}`;
          const links = data.links || {};
          const fsIds = links.fs_ids || [];

          // If this SOP references our FS, add it to reverse links
          if (fsIds.includes(spec.id)) {
            reverseLinks.sop_ids.push(id);
            titles[id] = data.title || id;
          }

          // If this SOP is in our forward links, add its title
          if (spec.links.sop_ids.includes(id)) {
            titles[id] = data.title || id;
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching SOP titles:', err);
        }
      }

      setEntityTitles(titles);
      setReverseLinkedEntities(reverseLinks);
    };

    fetchEntityTitles();
  }, [projectId, spec.id, spec.links, isExpanded]);

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { textKey: string; variant: 'success' | 'warning' | 'info' | 'error' }> = {
      Approved: { textKey: 'global_status_approved', variant: 'success' },
      'In Review': { textKey: 'global_status_in_review', variant: 'warning' },
      Draft: { textKey: 'global_status_draft', variant: 'info' },
      Retired: { textKey: 'global_status_rejected', variant: 'error' }
    };
    const statusInfo = statusMap[status] || { textKey: status, variant: 'info' as const };
    return <StatusBadge text={t(statusInfo.textKey)} variant={statusInfo.variant} size="medium" />;
  };

  const formatDate = (date: any) => {
    try {
      return new Date(date?.seconds ? date.seconds * 1000 : date).toLocaleDateString();
    } catch {
      return '';
    }
  };

  if (compact) {
    return (
      <div className={styles.specCardCompact}>
        <div className={styles.compactHeader}>
          <span className={styles.funcId}>{spec.id}</span>
          {getStatusBadge(spec.status)}
        </div>
        <p className={styles.compactTitle}>{spec.title}</p>
      </div>
    );
  }

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.cardActions}`)) {
      return;
    }
    if (target.closest('button') && !target.closest(`.${styles.cardHeader}`)) {
      return;
    }
    if (target.closest(`.${styles.entityLinkItem}`)) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      id={`entity-${spec.id}`}
      data-entity-card
      className={styles.specCard}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.headerLeft}>
          <h4 className={styles.funcId}>
            {spec.id}: {spec.title}
          </h4>
        </div>
        <div className={styles.headerRight}>
          {getStatusBadge(spec.status)}
          <div
            data-expand-trigger
            className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
          >
            <img
              src={isDarkMode ? "/assets/icons/chevron.down.svg" : "/assets/icons/chevron.down.black.svg"}
              alt={isExpanded ? "Collapse" : "Expand"}
              width={12}
              height={12}
            />
          </div>
        </div>
      </div>

      <p className={styles.description}>{spec.description}</p>

      <div className={`${styles.specDetails} ${isExpanded ? styles.expanded : styles.collapsed}`}>
        <div className={styles.specDetailsInner}>

          {/* Inline meta fields */}
          <div className={styles.inlineMeta}>

            {/* Design Hint */}
            {spec.design_hint && (
              <div>
                <span className={styles.metaLabel}>{t('project_tab_design_hint') || 'Design Hint'}:</span>
                <span className={styles.metaValue}> {spec.design_hint}</span>
              </div>
            )}

            {/* Category */}
            {spec.category && (
              <div>
                <span className={styles.metaLabel}>{t('project_tab_category')}:</span>
                <span className={styles.metaValue}> {spec.category}</span>
              </div>
            )}

            {/* GXP Relevance */}
            {spec.gxp_relevance && (
              <div>
                <span className={styles.metaLabel}>{t('project_tab_gxp_relevance')}:</span>
                <span className={styles.metaValue}> {spec.gxp_relevance}</span>
              </div>
            )}

            {/* Owner 
            {spec.meta?.owner && (
              <div>
                <span className={styles.metaValue}>{spec.meta.owner}</span>
              </div>
            )}*/}
          </div>

          {/* Inputs, Outputs, Preconditions, Postconditions */}
          {((spec.inputs && spec.inputs.length > 0) || (spec.outputs && spec.outputs.length > 0) || (spec.preconditions && spec.preconditions.length > 0) || (spec.postconditions && spec.postconditions.length > 0)) && (
            <div className={styles.inlineMeta}>
              {spec.inputs && spec.inputs.length > 0 && (
                <div>
                  <span className={styles.metaLabel}>{t('project_tab_inputs')}:</span>
                  <span className={styles.metaValue}> {spec.inputs.join(', ')}</span>
                </div>
              )}
              {spec.outputs && spec.outputs.length > 0 && (
                <div>
                  <span className={styles.metaLabel}>{t('project_tab_outputs')}:</span>
                  <span className={styles.metaValue}> {spec.outputs.join(', ')}</span>
                </div>
              )}
              {spec.preconditions && spec.preconditions.length > 0 && (
                <div>
                  <span className={styles.metaLabel}>{t('project_tab_preconditions')}:</span>
                  <span className={styles.metaValue}> {spec.preconditions.join(', ')}</span>
                </div>
              )}
              {spec.postconditions && spec.postconditions.length > 0 && (
                <div>
                  <span className={styles.metaLabel}>{t('project_tab_postconditions')}:</span>
                  <span className={styles.metaValue}> {spec.postconditions.join(', ')}</span>
                </div>
              )}
            </div>
          )}

          {/* Dates on separate line */}
          {(spec.createdAt || spec.updatedAt) && (
            <div className={styles.inlineMeta}>
              {spec.createdAt && (
                <div>
                  <span className={styles.metaLabel}>{t('global_created')}:</span>
                  <span className={styles.metaValue}> {formatDate(spec.createdAt)}</span>
                </div>
              )}
              {spec.updatedAt && (
                <div>
                  <span className={styles.metaLabel}>{t('global_updated')}:</span>
                  <span className={styles.metaValue}> {formatDate(spec.updatedAt)}</span>
                </div>
              )}
            </div>
          )}

          {/* Connected Entities - combine forward and reverse links */}
          {(() => {
            // Combine forward and reverse links, removing duplicates
            const allUrsIds = Array.from(new Set([...spec.links.urs_ids, ...reverseLinkedEntities.urs_ids]));
            const allTestIds = Array.from(new Set([...spec.links.test_ids, ...reverseLinkedEntities.test_ids]));
            const allRiskIds = Array.from(new Set([...spec.links.risk_ids, ...reverseLinkedEntities.risk_ids]));
            const allSopIds = Array.from(new Set([...spec.links.sop_ids, ...reverseLinkedEntities.sop_ids]));

            const hasAnyLinks = allUrsIds.length > 0 || allTestIds.length > 0 || allRiskIds.length > 0 || allSopIds.length > 0;

            if (!hasAnyLinks) return null;

            return (
              <div className={styles.connectedEntitiesSection}>
                {allUrsIds.length > 0 && (
                  <div className={styles.entityGroup}>
                    <span className={styles.entityGroupLabel}>{t('project_tab_linked_urs') || 'Linked URS'}:</span>
                    <div className={styles.entityList}>
                      {allUrsIds.map((id) => (
                        <button
                          key={id}
                          type="button"
                          className={styles.entityLinkItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEntityClick?.(id, 'URS');
                          }}
                        >
                          {id}: {entityTitles[id] || id}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {allTestIds.length > 0 && (
                  <div className={styles.entityGroup}>
                    <span className={styles.entityGroupLabel}>{t('project_tab_linked_tests') || 'Linked TEST'}:</span>
                    <div className={styles.entityList}>
                      {allTestIds.map((id) => (
                        <button
                          key={id}
                          type="button"
                          className={styles.entityLinkItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEntityClick?.(id, 'TEST');
                          }}
                        >
                          {id}: {entityTitles[id] || id}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {allRiskIds.length > 0 && (
                  <div className={styles.entityGroup}>
                    <span className={styles.entityGroupLabel}>{t('project_tab_linked_risks') || 'Linked RISK'}:</span>
                    <div className={styles.entityList}>
                      {allRiskIds.map((id) => (
                        <button
                          key={id}
                          type="button"
                          className={styles.entityLinkItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEntityClick?.(id, 'RISK');
                          }}
                        >
                          {id}: {entityTitles[id] || id}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {allSopIds.length > 0 && (
                  <div className={styles.entityGroup}>
                    <span className={styles.entityGroupLabel}>{t('project_tab_linked_sops') || 'Linked SOP'}:</span>
                    <div className={styles.entityList}>
                      {allSopIds.map((id) => (
                        <button
                          key={id}
                          type="button"
                          className={styles.entityLinkItem}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEntityClick?.(id, 'SOP');
                          }}
                        >
                          {id}: {entityTitles[id] || id}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Card actions */}
          <div
            className={styles.cardActions}
            onClick={(e) => e.stopPropagation()}
          >

            {/* Edit button */}
            {onEdit && (
              <GlobalMicroButton
                onClick={() => onEdit(spec)}
                icon={<img src={isDarkMode ? "/assets/icons/pencil.svg" : "/assets/icons/pencil.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_edit')}
              </GlobalMicroButton>
            )}

            {/* Delete button */}
            {onDelete && (
              <GlobalMicroButton
                onClick={() => onDelete(spec.id)}
                icon={<img src={isDarkMode ? "/assets/icons/trash.svg" : "/assets/icons/trash.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_delete')}
              </GlobalMicroButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

